import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Loader2, PackageCheck } from "lucide-react";
import api from "../lib/api";

const BUSINESS_TYPES = ["Dealer / Distributor", "Retailer", "Contractor", "Interior / Commercial Project", "Other"];

export function WholesaleEnquirySection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", business: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone"); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, source: "wholesale_section" });
      toast.success("Bulk enquiry received! We'll be in touch with wholesale pricing.");
      setForm({ name: "", phone: "", email: "", business: "", quantity: "", message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section className="px-4 py-16 sm:px-8">
      <div className="navy-grid-bg mx-auto grid max-w-7xl gap-10 overflow-hidden rounded-3xl border border-slate-800 p-8 text-white sm:p-12 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#6EE7B7]">
            Bulk & Wholesale
          </span>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
            Request Bulk Wholesale Pricing
          </h2>
          <p className="mt-4 max-w-md text-slate-300">
            Buying in volume for a store, project or distribution? Send us your requirement and our team
            will respond with factory-direct wholesale rates.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-300">
            {["Priority response for dealers & distributors", "Custom roll and tile sizes", "No minimum-order surprises — we quote clearly"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <PackageCheck className="h-5 w-5 text-[#10B981]" /> {t}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} data-testid="wholesale-enquiry-form"
          className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-xl sm:p-8">
          <div className="grid gap-4">
            <div>
              <Label>Name *</Label>
              <Input data-testid="enquiry-input-name" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Phone *</Label>
                <Input data-testid="enquiry-input-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" />
              </div>
              <div>
                <Label>Business Type</Label>
                <Select value={form.business} onValueChange={(v) => setForm((f) => ({ ...f, business: v }))}>
                  <SelectTrigger data-testid="enquiry-select-business-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Required Quantity</Label>
              <Input data-testid="enquiry-input-quantity" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 200 pcs / 10 rolls" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="enquiry-input-email" type="email" value={form.email} onChange={set("email")} placeholder="We'll send you a confirmation" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea data-testid="enquiry-input-message" value={form.message} onChange={set("message")} placeholder="Products & details you need..." rows={3} />
            </div>
            <Button type="submit" disabled={loading} data-testid="enquiry-submit-button"
              className="bg-[#10B981] font-bold hover:bg-[#059669]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Request Wholesale Quote
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
